import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  Event as RouterEvent
} from '@angular/router';
import { concat, Observable, of, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, takeUntil } from 'rxjs/operators';

export interface LoadingBarState {
  progress: number;
  visible: boolean;
  instant: boolean;
}

const LOADING_BAR_IDLE: LoadingBarState = { progress: 0, visible: false, instant: true };
const LOADING_BAR_STEP_MS = 300;
const LOADING_BAR_FINISH_MS = 300;

const isNavigationFinished = (event: RouterEvent): boolean =>
  event instanceof NavigationEnd ||
  event instanceof NavigationCancel ||
  event instanceof NavigationError ||
  event instanceof NavigationSkipped;

export function navigationLoading$(router: Router, delayMs = 300): Observable<boolean> {
  return router.events.pipe(
    filter((event) => event instanceof NavigationStart || isNavigationFinished(event)),
    switchMap((event) => {
      if (!(event instanceof NavigationStart)) {
        return of(false);
      }

      return timer(delayMs).pipe(
        map(() => true),
        takeUntil(router.events.pipe(filter(isNavigationFinished)))
      );
    }),
    startWith(false)
  );
}

export function navigationLoadingBar$(router: Router): Observable<LoadingBarState> {
  return navigationLoading$(router).pipe(
    distinctUntilChanged(),
    skip(1),
    switchMap((loading) => (loading ? trickleLoadingBar$() : finishLoadingBar$())),
    startWith(LOADING_BAR_IDLE)
  );
}

function trickleLoadingBar$(): Observable<LoadingBarState> {
  return concat(
    of({ progress: 0, visible: true, instant: true }),
    timer(50, LOADING_BAR_STEP_MS).pipe(map((step) => ({ progress: 0.9 - 0.6 * 0.8 ** step, visible: true, instant: false })))
  );
}

function finishLoadingBar$(): Observable<LoadingBarState> {
  return concat(
    of({ progress: 1, visible: true, instant: false }),
    timer(LOADING_BAR_FINISH_MS).pipe(map(() => ({ progress: 1, visible: false, instant: false }))),
    timer(LOADING_BAR_FINISH_MS).pipe(map(() => LOADING_BAR_IDLE))
  );
}
